import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  BackgroundWrapper,
  reduceEmptySpaces,
  SectionHeading,
  SectionLayout,
} from "../../section.component"
import {
  QuickStartListContainer,
  QuickStartListContainerInsideFiveColumn,
  QuickStartListContainerInsideFourColumn,
  QuickStartListContainerInsideThreeColumn,
  QuickStartListContainerInsideTwoColumn,
  QuickStartListContainerInsideUgc,
  StyledCard,
  StyledEnrollLink,
} from "./quick-start.styled"
import ListItem from "../../list/list-item.component"
import {
  ListItemLayout,
  ListItemType,
  ListItem as ContentfulListItem,
  AccordionItem as ContentfulAccordionItem,
  NewsItem as ContentfulNewsItem,
  CareerOffer as ContentfulCareerOffer,
  ListBuilderWithHash,
  RenderRichText,
  AppFunctionComponent,
} from "../../../../../types"
import useTranslationHook from "../../../../../hooks/use-translation.hook"
import QuickStartMobileControls from "./quick-start-mobile-controls.component"
import { navigate } from "gatsby-link"
import SectionBackgroundWrapper from "../../../../../helpers/sitebuilder-background-wrapper/section-background-wrapper"
import RichText from "../../../../../resolvers/rich-text/rich-text.resolver"
import styled, { css } from "styled-components"
import NewsItem from "../../../../../ui/components/news-item/news-item.component"
import CareerOffers from "../../../../career/career-offers.component"
import { interactiveAccordion } from "../../../../../ui/helpers/interactive-accordion/interactive-accordion-item.hook"
import { kebabCase } from "lodash"
import AccordionItem from "../../../../../ui/components/accordion/accordion-item.component"
import UgcItem from "../../../../../ui/components/ugc-item/ugc-item.component"

const SVGExpandIcon: AppFunctionComponent = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M6.29303 9.70697L12 15.414L17.707 9.70697L16.293 8.29297L12 12.586L7.70703 8.29297L6.29303 9.70697Z"
      fill="black"
    />
  </svg>
)

const HeadingText = styled(SectionHeading)<{
  customStyle?: ListBuilderWithHash["style"]
}>`
  max-width: var(--text-max-width);
  padding: 32px 0 0 0;
  //   margin-bottom: 40px;

  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    padding: 32px 0;
  }

  h1 {
    margin-top: 0 !important;
    /* margin-bottom: 50px !important; */
  }
  h2 {
    font-family: Facto;
    font-size: 30px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
    margin-top: 0 !important;
  }
  p {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  ${reduceEmptySpaces};

  ${({ customStyle }) =>
    customStyle?.customerLovedKompakt &&
    css`
      background: red;
    `}
`

interface QuickStartRevealListProps {
  revealList: ListBuilderWithHash[]
  setRevealedSectionId?: React.Dispatch<React.SetStateAction<string | null>>
  revealedSectionId?: string | null
  grayscale?: boolean
  style?: ListBuilderWithHash["style"]
  text?: RenderRichText
  anchorId?: string
}

const RevealListContainerResolver: AppFunctionComponent<{
  itemsLength?: number
  isUgc?: boolean
}> = ({ itemsLength, children, isUgc }) => {
  if (isUgc && itemsLength !== undefined && itemsLength > 0) {
    return (
      <QuickStartListContainerInsideUgc $length={itemsLength} $width={330}>
        {children}
      </QuickStartListContainerInsideUgc>
    )
  }

  switch (itemsLength) {
    case 2:
      return (
        <QuickStartListContainerInsideTwoColumn>
          {children}
        </QuickStartListContainerInsideTwoColumn>
      )
    case 3:
      return (
        <QuickStartListContainerInsideThreeColumn>
          {children}
        </QuickStartListContainerInsideThreeColumn>
      )
    case 4:
      return (
        <QuickStartListContainerInsideFourColumn>
          {children}
        </QuickStartListContainerInsideFourColumn>
      )
    case 5:
      return (
        <QuickStartListContainerInsideFiveColumn>
          {children}
        </QuickStartListContainerInsideFiveColumn>
      )
    case 6:
      return (
        <QuickStartListContainerInsideThreeColumn>
          {children}
        </QuickStartListContainerInsideThreeColumn>
      )

    case 8:
      return (
        <QuickStartListContainerInsideFourColumn>
          {children}
        </QuickStartListContainerInsideFourColumn>
      )
    default:
      return (
        <QuickStartListContainerInsideThreeColumn>
          {children}
        </QuickStartListContainerInsideThreeColumn>
      )
  }
}

const QuickStartRevealList: AppFunctionComponent<QuickStartRevealListProps> = ({
  revealList,
  style,
  grayscale,
  revealedSectionId,
  setRevealedSectionId,
  text,
  anchorId,
}) => {
  const { translate } = useTranslationHook()
  const ref = useRef<HTMLDivElement | null>(null)

  const [currentIndex, setCurrentIndex] = useState(0)
  const [disabledPrev, setDisabledPrev] = useState(true)
  const [disabledNext, setDisabledNext] = useState(false)
  const linkElements = useMemo(
    () =>
      revealList.every((list) => typeof list?.hash === "string")
        ? revealList.map((list) => list.hash!)
        : undefined,
    [revealList]
  )

  const handlePrevClick = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => --prev)
      linkElements && void navigate(linkElements[currentIndex - 1])
    }
  }, [currentIndex, linkElements])

  const handleNextClick = useCallback(() => {
    if (currentIndex < revealList.length - 1) {
      setCurrentIndex((prev) => ++prev)
      linkElements && void navigate(linkElements[currentIndex + 1])
    }
  }, [currentIndex, linkElements, revealList])

  const hash = typeof window !== "undefined" ? window.location.hash : null
  useEffect(() => {
    if (linkElements) {
      if (hash) {
        setRevealedSectionId && setRevealedSectionId(hash)
        setCurrentIndex(linkElements.findIndex((el) => el === hash))
      } else {
        setRevealedSectionId && setRevealedSectionId(linkElements[0])
        void navigate(linkElements[0])
        setCurrentIndex(0)
      }
    }
    currentIndex === 0 ? setDisabledPrev(true) : setDisabledPrev(false)
    currentIndex === revealList.length - 1
      ? setDisabledNext(true)
      : setDisabledNext(false)
  }, [hash, linkElements, currentIndex])

  const handleScrollClick = () => {
    ref.current?.scrollIntoView()
  }

  const resolveItems = (
    item:
      | ContentfulListItem
      | ContentfulAccordionItem
      | ContentfulNewsItem
      | ContentfulCareerOffer
  ) => {
    const type = item.internal.type

    switch (type) {
      case ListItemType.Item:
        if (item.type === "ugc") {
          const { image } = item as ContentfulListItem
          return <UgcItem image={image as any} />
        }
        return (
          <ListItem
            key={item.id}
            imageWidth={style?.item_image_width}
            background={style?.item_background}
            layout={ListItemLayout.VerticalCentered}
            {...item}
          />
        )

      case ListItemType.AccordionItem:
        const accordionItem = item as ContentfulAccordionItem
        const text = accordionItem.accordionText ? (
          <RichText content={accordionItem.accordionText} />
        ) : null
        const slug = kebabCase(accordionItem.title)

        const props = {
          title: accordionItem.title,
          link: location.pathname || "",
          text,
          slug,
        }
        return (
          <AccordionItem
            key={slug}
            {...props}
            {...interactiveAccordion(props)}
          />
        )

      case ListItemType.NewsItem:
        return (
          <NewsItem
            {...(item as ContentfulNewsItem)}
            key={item.id}
            fullHeight
          />
        )

      case ListItemType.CareerOffer:
        return (
          <CareerOffers {...(item as ContentfulCareerOffer)} key={item.id} />
        )

      default:
        return <></>
    }
  }

  const isUgc = revealList.every((list) => list?.type === "ugc")

  return (
    <QsBackgroundWrapper id={anchorId}>
      <SectionBackgroundWrapper customStyle={style}>
        <QuickStartListContainer
          layout={SectionLayout.Text}
          background={style?.background ?? "transparent"}
          style={style}
          ref={ref}
          isUgc={isUgc}
        >
          {text && JSON.parse(text?.raw)?.content.length > 1 && (
            <HeadingText>
              <RichText content={text} />
            </HeadingText>
          )}
          <RevealListContainerResolver
            itemsLength={revealList.length}
            isUgc={isUgc}
          >
            {revealList.map((listItem, index) => {
              return (
                <StyledCard
                  id={listItem.hash}
                  key={listItem.id}
                  activeHashName={revealedSectionId}
                  grayscale={grayscale}
                  index={index}
                  currentIndex={currentIndex}
                  customStyle={listItem.style}
                >
                  {resolveItems(listItem as unknown as ContentfulListItem)}

                  {listItem.hash && (
                    <StyledEnrollLink
                      activeHashName={revealedSectionId}
                      to={listItem.hash}
                      onClick={handleScrollClick}
                    >
                      {translate("Read More")} <SVGExpandIcon />
                    </StyledEnrollLink>
                  )}
                </StyledCard>
              )
            })}
          </RevealListContainerResolver>
          <QuickStartMobileControls
            color={style?.color}
            disabledNext={disabledNext}
            disabledPrev={disabledPrev}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        </QuickStartListContainer>
      </SectionBackgroundWrapper>
    </QsBackgroundWrapper>
  )
}

export default QuickStartRevealList

const QsBackgroundWrapper = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    /* display: none; */
  }

  ${BackgroundWrapper} {
    ${BackgroundWrapper} {
      padding-left: 0px !important;
      padding-right: 0px !important;
    }
  }
`
